import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import TextButton from 'LuxKit/Button/TextButton'
import AccommodationPackageTilePricingBlock from 'components/AccommodationKit/AccommodationPackageTilePricingBlock'
import { getDisplayTotalPrice, aggregateBedbankMultiRoomPrices } from 'lib/hotels/bedbankUtils'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { useAppSelector } from 'hooks/reduxHooks'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { useBedbankDiscountFromPriceAndValue } from 'hooks/useBedbankDiscount'
import Group from 'components/utils/Group'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getSource } from 'selectors/offerSelectors'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import ModalContext from 'contexts/ModalContext'
import { isMetasearchRateSource } from 'lib/metasearch/metasearchSource'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorBedbankOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'
import { FLIGHTS_MAX_LEAD_TIME_DAYS } from 'constants/flight'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import OfferPageBurnPriceDisplay from 'components/OfferPage/Loyalty/OfferPageBurnPriceDisplay'
import { isOfferPageFullyPaidWithPoints } from 'lib/offer/isOfferPageFullyPaidWithPoints'
import OfferPagePaidEntirelyWithPointsLabel from 'components/OfferPage/Loyalty/OfferPagePaidEntirelyWithPointsLabel'
import { arrayToObject } from 'lib/array/arrayUtils'
import type { RoomWithData } from 'hooks/OfferPage/useBedbankBuyCallbacks'
import Caption from 'LuxKit/Typography/Caption'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useRoomOptionRateBedbankQuote from 'hooks/Insurance/useRoomOptionRateBedbankQuote'
import BedbankRoomOptionRateInsurance from '../BedbankRoomOptionRateInsurance'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useIntl, FormattedMessage } from 'react-intl'
import { formatSaleUnitCount } from 'hooks/useSaleUnitFormatting'
import useOfferPageBurnPoints, { useOfferPageBundledBurnPoints } from 'hooks/useOfferPageBurnPoints'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'

const FlexAutoGroup = styled(Group)`
  & > * {
    flex: auto;
  }
`

const LoyaltyPointsWrapper = styled.div`
  &:empty {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

interface Props {
  rooms: Array<OfferPageRoom>
  roomsWithData: Array<RoomWithData>
  offer: App.BedbankOffer | App.BedbankOfferSummary
  flightOrigin?: App.Airport
  flightPrice?: number
  checkIn?: string
  departureTooEarly?: boolean
  flightsEnabled?: boolean
  luxPlusLPPEnabled: boolean
  isFlightsSelected: boolean
  isLoggedIn: boolean
  canProceed: boolean
  onBookNow: (keepBrowsing?: boolean, isInsuranceSelected?: boolean, roomsWithData?: Array<RoomWithData>) => void
  onAddToUserCartNow: (roomsWithData?: Array<RoomWithData>, isInsuranceSelected?: boolean) => void
}

function BedbankMultiRoomPricing(props: Props) {
  const {
    rooms,
    roomsWithData,
    offer,
    checkIn,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    onBookNow,
    onAddToUserCartNow,
    luxPlusLPPEnabled,
    isFlightsSelected,
    isLoggedIn,
    canProceed,
  } = props

  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)

  const showModal = useContext(ModalContext)
  const offerPageDispatch = useOfferPageDispatch()
  const hasFlights = !!flightPrice
  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch])

  const departureTooFarInFuture = useMemo(() => {
    if (checkIn) {
      return moment(checkIn).diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS
    }
    return false
  }, [checkIn])

  const hasDates = !!checkIn

  const {
    propertyFees,
    bundlePropertyFees,
    hotelPrice,
    hotelMemberPrice,
    bundlePrice,
    bundleMemberPrice,
    hotelValue,
    bundleValue,
  } = useMemo(
    () => aggregateBedbankMultiRoomPrices(roomsWithData),
    [roomsWithData],
  )

  const totalsByRoom = useMemo(() => arrayToObject(
    roomsWithData,
    roomData => roomData.room.id,
    roomData => {
      const rate = roomData.rate
      const bundleRate = roomData.bundleRate
      const { propertyFees = 0 } = rate.totals
      const bundlePropertyFees = bundleRate?.totals.propertyFees || propertyFees

      const hotelPrice = rate.totals.inclusive
      const hotelMemberPrice = rate.totals.memberInclusive ?? 0
      const bundlePrice = bundleRate?.totals.inclusive || hotelPrice

      return getDisplayTotalPrice({
        hotelPrice: hotelPrice + propertyFees,
        hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
        hotelValue: rate.value,
        bundlePrice: bundlePrice + bundlePropertyFees,
        bundleValue: bundleRate?.value,
        checkIn,
        flightPrice: flightPrice ? flightPrice / roomsWithData.length : undefined,
        rooms: [roomData.room],
      })
    },
  ), [checkIn, flightPrice, roomsWithData])

  const hasPromoRate = roomsWithData.some(roomData => !!roomData.promotion)

  const canAddToUserCart = hasDates

  const totalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
    hotelValue,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue,
    checkIn,
    flightPrice,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })
  const bundleDisplayTotals = getDisplayTotalPrice({
    hotelPrice: bundlePrice + bundlePropertyFees,
    hotelMemberPrice: bundleMemberPrice > 0 ? bundleMemberPrice + bundlePropertyFees : 0,
    hotelValue: bundleValue,
    checkIn,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })
  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : 'total'
  const loyaltyProductType = getLoyaltyProductType(offer) ?? 'hotel_lpp_eps'
  // Two burn calcs: tile shows hotel-only + hotel+flight rows with different rates.
  const hotelBurnResult = useOfferPageBurnPoints(totalPrices.totals.price, loyaltyProductType)
  const displayFlightPrice = totalPrices.totals.flightBundlePrice - bundleDisplayTotals.totals.price
  const canShowFlightBundlePrice = flightsEnabled && hasFlights && !departureTooEarly
  const flightBundleBurnResult = useOfferPageBundledBurnPoints({
    hotelPrice: bundleDisplayTotals.totals.price,
    hotelLoyaltyProductType: loyaltyProductType,
    flightPrice: displayFlightPrice,
    shouldUseSplitBurn: !!canShowFlightBundlePrice,
  })

  const discount = useBedbankDiscountFromPriceAndValue(totalPrices.totals.value, totalPrices.totals.price, totalPrices.totals.memberPrice)
  const source = useAppSelector(state => getSource(state))

  const nights = roomsWithData[0].effectiveRate.nights

  // Get CFMR quote for flex packages
  const checkout = useMemo(() => {
    if (!checkIn || !nights) return undefined
    return moment(checkIn).add(nights, 'days').format(ISO_DATE_FORMAT)
  }, [checkIn, nights])
  const canTriggerFlexCfmrBedbankPackagesExperiment = useMemo(() => {
    if (!roomsWithData || !checkIn || !checkout) return false
    const effectiveRates = roomsWithData.map(roomWithData => roomWithData.effectiveRate)
    const allRoomsAreNonRefundable = effectiveRates.every(rate => !rate.partiallyRefundable && !rate.refundable)
    return allRoomsAreNonRefundable && !!checkIn && !!checkout
  }, [checkIn, checkout, roomsWithData])
  const isFlexCfmrBedbankPackagesEnabled = useOptimizelyExperiment(OptimizelyExperiments.croFlexCfmrBedbankPackages, canTriggerFlexCfmrBedbankPackagesExperiment)
  const { quote, isFetching: isQuoteFetching, error: quoteError, onSelectInsurance, isInsuranceSelected } = useRoomOptionRateBedbankQuote({
    country: offer.property.address.countryName,
    coverAmount: totalPrices.totals.price,
    checkIn,
    checkout,
    travellers: rooms,
    useCache: true,
    disabled: !isFlexCfmrBedbankPackagesEnabled,
  })

  const onBookNowHandler = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const keepBrowsing: boolean = !!event.currentTarget.dataset.keepBrowsing
    onBookNow(keepBrowsing, isInsuranceSelected)
  }, [onBookNow, isInsuranceSelected])

  const onAddToUserCartHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    onAddToUserCartNow(undefined, isInsuranceSelected)
  }, [onAddToUserCartNow, isInsuranceSelected])

  const isMetaSearch = useMemo(() => {
    return isMetasearchRateSource(source)
  }, [source])

  const onLoginForMemberSavings = useCallback(async() => {
    showModal<AccountAccessModalResult>(<AccountAccessModal />)
  }, [showModal])

  const shouldDisplayLoginForMemberSavings = !isLoggedIn && isMetaSearch

  const showHotelAndFlightsToggle = flightsEnabled

  const withFlights = flightsEnabled && isFlightsSelected && !!flightOrigin

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return [
      ...roomsWithData.map(roomData => {
        const rate = roomData.effectiveRate
        const { propertyFees = 0 } = rate.totals
        const roomTotalPrices = totalsByRoom[roomData.room.id]

        return (
          generateLoyaltyPointsCalculatorBedbankOptions(rate, {
            price: roomTotalPrices.totals.price,
            memberPrice: roomTotalPrices.totals.memberPrice,
            propertyFees,
            propertyId: offer.property.id,
            offerId: offer.id,
            propertyCountryCode: offer.property.address.countryCode,
            propertyState: offer.property.address.stateProvinceCode,
            currentRegionCode,
          })
        )
      }),
      generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
        flightPrice,
        skip: !withFlights,
      }),
    ]
  }, [
    totalsByRoom,
    roomsWithData,
    offer,
    flightPrice,
    withFlights,
    currentRegionCode,
  ])

  const checkAvailabilityText = intl.formatMessage({
    id: 'ArdTQehkF0v1QCe8Stv4',
    defaultMessage: 'Check availability',
    description: 'Button text to check availability on bedbank multi room pricing',
  })
  const bookNowText = intl.formatMessage({
    id: 'YBfMSkP4jTEtXeOQETMk',
    defaultMessage: 'Book now',
    description: 'Button text to book now on bedbank multi room pricing',
  })
  const hotelOnlyBookNowText = rooms.length > 1
    ? intl.formatMessage(
        {
          id: '7nlh96x-LRG_KjRtMHRU',
          defaultMessage: 'Book {count, plural, one {# room} other {# rooms}}',
          description: 'Button text to book multiple rooms on bedbank multi room pricing',
        },
        { count: rooms.length },
      )
    : bookNowText
  const selectRoomsText = intl.formatMessage(
    {
      id: 'oghQDmIfhfvIKseGShQa',
      defaultMessage: 'Select {count, plural, one {# room} other {# rooms}}',
      description: 'Button text to select rooms on bedbank multi room pricing with flights',
    },
    { count: rooms.length },
  )

  const isMultiRoom = rooms.length > 1
  const roomsLabel = formatSaleUnitCount(intl, offer.saleUnit, rooms.length)

  const priceCaptionSingleNoFlightsNoDates = intl.formatMessage(
    {
      id: 'YM2kAmn-rGniyHmRSvt8',
      defaultMessage: '{nights, plural, one {# night} other {# nights}}',
      description: 'Price caption showing nights count for a single room without flights or from label on bedbank multi-room pricing',
    },
    { nights },
  )
  const priceCaptionSingleNoFlightsWithDates = intl.formatMessage(
    {
      id: 'opb0P7A3EUhQvcSCEkSb',
      defaultMessage: '{nights, plural, one {# night} other {# nights}} from',
      description: 'Price caption showing nights count with from label for a single room on bedbank multi-room pricing',
    },
    { nights },
  )
  const priceCaptionMultiNoFlightsNoDates = intl.formatMessage(
    {
      id: 'iVf_Ud4Uh3mR5-571AtY',
      defaultMessage: '{nights, plural, one {# night} other {# nights}}, {rooms}',
      description: 'Price caption showing nights count and room count without flights or from label on bedbank multi-room pricing',
    },
    { nights, rooms: roomsLabel },
  )
  const priceCaptionMultiNoFlightsWithDates = intl.formatMessage(
    {
      id: 'WyQaskHXEQ_ROsxk4pCj',
      defaultMessage: '{nights, plural, one {# night} other {# nights}}, {rooms} from',
      description: 'Price caption showing nights count and room count with from label on bedbank multi-room pricing',
    },
    { nights, rooms: roomsLabel },
  )

  const priceCaptionSingleWithFlights = intl.formatMessage(
    {
      id: '2zTPU-GGX0PikKiZOIY-',
      defaultMessage: '{nights, plural, one {# night + flights} other {# nights + flights}} from',
      description: 'Price caption showing nights count with flights for a single room on bedbank multi-room pricing',
    },
    { nights },
  )
  const priceCaptionMultiWithFlights = intl.formatMessage(
    {
      id: '7iINh5-3vVOvPU2G4tFD',
      defaultMessage: '{nights, plural, one {# night} other {# nights}}, {rooms} + flights from',
      description: 'Price caption showing nights count, room count, and flights on bedbank multi-room pricing',
    },
    { nights, rooms: roomsLabel },
  )

  if (withFlights) {
    return (
      <VerticalSpacer gap={12}>
        {!!isFlexCfmrBedbankPackagesEnabled && <div>
          <BedbankRoomOptionRateInsurance
            isLoading={isQuoteFetching}
            onChange={onSelectInsurance}
            errorOnQuote={!!quoteError}
            quote={quote}
            refundTitle={intl.formatMessage({
              id: 'bTCuzcNWKDe-utOavIGE',
              defaultMessage: 'Full refund for unforeseen events',
              description: 'Insurance refund title for bedbank room option rate',
            })}
            refundDetails={intl.formatMessage({
              id: 'JLlAlfazJ_IIv-Scu750',
              defaultMessage: '(Hotel only)',
              description: 'Insurance refund detail indicating hotel only coverage for bedbank room option rate',
            })}
          />
        </div>}
        <AccommodationPackageTilePricingBlock
          tabletDirection="horizontal"
          tabletVerticalAlign="center"
          data-testid="flights-pricing"
          title={undefined}
          callToAction={<FlexAutoGroup direction="horizontal" wrap="wrap" gap={8} noDisplayWhenEmpty>
            {flightOrigin.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly && <>
              {canAddToUserCart && <TextButton
                size="medium"
                kind="primary"
                data-testid="book-button"
                onClick={onAddToUserCartHandler}
                disabled={!canProceed}
              >
                {selectRoomsText}
              </TextButton>}
              {!canAddToUserCart && <TextButton
                size="medium"
                kind="primary"
                data-testid="book-button"
                onClick={onBookNowHandler}
                disabled={!canProceed}
              >
                {hasDates ? bookNowText : checkAvailabilityText}
              </TextButton>}
            </>}
            {!hasFlights && flightOrigin.code === offer.flights?.airportCode && <TextButton
              size="medium"
              data-filter="airport"
              kind="tertiary"
              onClick={onToggleFilter}
            >
              {intl.formatMessage({
                id: 'dTzyRXWP7NZN-Z71-qi-',
                defaultMessage: 'Change airport',
                description: 'Change airport button text for bedbank multi room pricing',
              })}
            </TextButton>}
            {flightOrigin.code !== offer.flights?.airportCode && (departureTooEarly || departureTooFarInFuture) && <TextButton
              size="medium"
              data-filter="checkin"
              kind="tertiary"
              onClick={onToggleFilter}
            >
              {intl.formatMessage({
                id: '8XN9HuhRPIxWQUmZiDG2',
                defaultMessage: 'Change departure date',
                description: 'Change departure date button text for bedbank multi room pricing',
              })}
            </TextButton>}
          </FlexAutoGroup>}
        >
          <VerticalSpacer gap={12}>
            {!hasFlights && flightOrigin.code === offer.flights?.airportCode && <BodyText
              variant="small"
              colour="light-foreground-alt"
            >
              {intl.formatMessage({
                id: 'b544xJUAyEA_feIc6aTT',
                defaultMessage: 'No flights available from {airportName}. Please select another airport.',
                description: 'Error message when no flights are available from the selected airport on bedbank offer page',
              }, { airportName: flightOrigin.name })}
            </BodyText>}
            {!hasFlights && departureTooFarInFuture && (
              <BodyText variant="small" colour="light-foreground-alt">
                {intl.formatMessage({
                  id: 'nW-s6s_wrzcn0c2oITdD',
                  defaultMessage: 'Flights can only be booked up to 11 months in advance.',
                  description: 'Warning that flights cannot be booked more than 11 months ahead',
                })}
              </BodyText>
            )}
            {flightOrigin.code !== offer.flights?.airportCode && departureTooEarly && <BodyText
              variant="small"
              colour="light-foreground-alt"
            >
              {intl.formatMessage({
                id: 'H-dmnQDd0PrQGtb5myyv',
                defaultMessage: 'Flights cannot be booked online when check-in is within 24 hours.',
                description: 'Warning that flights are unavailable when check-in is less than 24 hours away',
              })}
            </BodyText>}
          </VerticalSpacer>

          {!hasFlights && flightOrigin.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly && (
            <VerticalSpacer gap={12}>
              <div>
                <PriceRowPriceCaption>
                  {isMultiRoom ? (hasDates ? priceCaptionMultiNoFlightsWithDates : priceCaptionMultiNoFlightsNoDates) : (hasDates ? priceCaptionSingleNoFlightsWithDates : priceCaptionSingleNoFlightsNoDates)}
                </PriceRowPriceCaption>
                <Group direction="horizontal" verticalAlign="center" gap={4}>
                  <LuxPlusPriceGuard
                    showMemberPrice={(bundleMemberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                    fallbackPrice={<PriceRowPrice
                      data-testid="bedbank-package-price"
                      size="M"
                      price={bundlePrice}
                      saleUnit=""
                    />}
                  >
                    <LuxPlusPriceStack
                      size="M"
                      price={bundlePrice}
                      memberPrice={bundleMemberPrice}
                      saleUnit=""
                    />
                  </LuxPlusPriceGuard>
                  <Caption variant="large">
                    <FormattedMessage
                      id="Byn3qMv9JoNroggoi2MT"
                      defaultMessage="plus flights"
                      description="Label indicating flights are additional on bedbank multi-room pricing"
                    />
                  </Caption>
                </Group>
                {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}

                {roomsWithData.map(roomData => {
                  return (
                    <PriceRowAgentHubCommission
                      key={roomData.room.id}
                      productType={offer.productType}
                      offerLocations={[]}
                      offerId={offer.id}
                      packageId={roomData.effectiveRate.id}
                      offerTotal={totalsByRoom[roomData.room.id].original.flightBundlePrice}
                      kind="plain"
                    />
                  )
                })}

                {!!discount && <CSSBreakpoint max="mobile">
                  <PriceRowValueDiscountWithCaption
                    size="M"
                    originalValue={bundleValue}
                    discountPercentage={discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? discount : undefined}
                    discountType="estimate"
                  />
                </CSSBreakpoint>}
              </div>
              <LoyaltyPointsWrapper>
                <LoyaltyPointsEarnWithBreakdownModal
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType={!checkIn ? 'estimate' : 'exact'}
                  pointsToApply={hotelBurnResult.pointsToBurn}
                  burnPriceInPoints={hotelBurnResult.priceInPoints}
                />
              </LoyaltyPointsWrapper>
            </VerticalSpacer>
          )}

          {canShowFlightBundlePrice && <VerticalSpacer gap={12}>
            <div>
              {isOfferPageFullyPaidWithPoints(
                flightBundleBurnResult.pointsToBurn,
                flightBundleBurnResult.priceInPoints,
                flightBundleBurnResult.hideZeroCash,
              ) && <OfferPagePaidEntirelyWithPointsLabel />}
              <PriceRowPriceCaption startIcon={showHotelAndFlightsToggle ? <LinePlaneDepartureIcon size="S" /> : undefined}>
                {isMultiRoom ? priceCaptionMultiWithFlights : priceCaptionSingleWithFlights}
              </PriceRowPriceCaption>
              {flightBundleBurnResult.enabled && <OfferPageBurnPriceDisplay
                totalPrice={totalPrices.totals.flightBundlePrice}
                pointsToApply={flightBundleBurnResult.pointsToBurn}
                priceInPoints={flightBundleBurnResult.priceInPoints}
                pointsHideZeroCash={flightBundleBurnResult.hideZeroCash}
                memberPrice={totalPrices.totals.flightBundleMemberPrice}
                size="M"
                saleUnit={offer.displayPricingAsPerPerson ? 'person' : 'total'}
              />}
              {!flightBundleBurnResult.enabled && <LuxPlusPriceGuard
                showMemberPrice={(totalPrices.totals.flightBundleMemberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                fallbackPrice={<PriceRowPrice
                  size="M"
                  price={totalPrices.totals.flightBundlePrice}
                  saleUnit={saleUnit}
                />}
              >
                <LuxPlusPriceStack
                  size="M"
                  price={totalPrices.totals.flightBundlePrice}
                  memberPrice={totalPrices.totals.flightBundleMemberPrice}
                  saleUnit={offer.displayPricingAsPerPerson ? 'person' : 'total*'}
                />
              </LuxPlusPriceGuard>}
              {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
              <PriceRowTaxesAndFees />
              {discount > 0 && !hasPromoRate && <CSSBreakpoint max="mobile">
                <PriceRowValueDiscountWithCaption
                  size="M"
                  originalValue={totalPrices.totals.flightBundleValue}
                  discountPercentage={discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? discount : undefined}
                  discountType="estimate"
                />
              </CSSBreakpoint>}
            </div>
            <LoyaltyPointsWrapper>
              <LoyaltyPointsEarnWithBreakdownModal
                calculationRequests={pointsEarnCalculationRequests}
                calculationType="estimate"
                pointsToApply={flightBundleBurnResult.pointsToBurn}
                burnPriceInPoints={flightBundleBurnResult.priceInPoints}
              />
            </LoyaltyPointsWrapper>
          </VerticalSpacer>}
          <AtolProtectedTooltip />
        </AccommodationPackageTilePricingBlock>
      </VerticalSpacer>)
  } else {
    return (
      <VerticalSpacer gap={12}>
        {!!isFlexCfmrBedbankPackagesEnabled && <div>
          <BedbankRoomOptionRateInsurance
            isLoading={isQuoteFetching}
            onChange={onSelectInsurance}
            errorOnQuote={!!quoteError}
            quote={quote}
            refundTitle={intl.formatMessage({
              id: 'bTCuzcNWKDe-utOavIGE',
              defaultMessage: 'Full refund for unforeseen events',
              description: 'Insurance refund title for bedbank room option rate',
            })}
            refundDetails={intl.formatMessage({
              id: 'JLlAlfazJ_IIv-Scu750',
              defaultMessage: '(Hotel only)',
              description: 'Insurance refund detail indicating hotel only coverage for bedbank room option rate',
            })}
          />
        </div>}
        <AccommodationPackageTilePricingBlock
          tabletDirection="horizontal"
          tabletVerticalAlign="center"
          data-testid="pricing"
          title={undefined}
          callToAction={<FlexAutoGroup direction="horizontal" wrap="wrap" gap={8} noDisplayWhenEmpty>
            {shouldDisplayLoginForMemberSavings && <TextButton size="large" kind="primary" nonInteractable onClick={onLoginForMemberSavings}>
              {intl.formatMessage({
                id: 'gDdtqhHswaUNw2fCvVGT',
                defaultMessage: 'Login for member savings',
                description: 'Button text to login for member savings on bedbank multi room pricing',
              })}
            </TextButton>}
            {canAddToUserCart && (
              <TextButton
                size="medium"
                kind="secondary"
                onClick={onAddToUserCartHandler}
                startIcon={<LineShoppingCartIcon />}
                disabled={!canProceed}
              >
                {intl.formatMessage({
                  id: '81iw4AbS7Q_M-_VcjTSe',
                  defaultMessage: 'Add to cart',
                  description: 'Add to cart button text for bedbank multi room pricing',
                })}
              </TextButton>
            )}
            {!shouldDisplayLoginForMemberSavings && <TextButton
              size="medium"
              kind="primary"
              data-testid="book-button"
              onClick={onBookNowHandler}
              disabled={!canProceed}
            >
              {hasDates ? hotelOnlyBookNowText : checkAvailabilityText}
            </TextButton>}
          </FlexAutoGroup>}
        >
          <VerticalSpacer gap={12}>
            <div>
              {isOfferPageFullyPaidWithPoints(
                hotelBurnResult.pointsToBurn,
                hotelBurnResult.priceInPoints,
                hotelBurnResult.hideZeroCash,
              ) && <OfferPagePaidEntirelyWithPointsLabel />}
              <PriceRowPriceCaption>
                {isMultiRoom
                  ? (hasDates ? priceCaptionMultiNoFlightsWithDates : priceCaptionMultiNoFlightsNoDates)
                  : (hasDates ? priceCaptionSingleNoFlightsWithDates : priceCaptionSingleNoFlightsNoDates)}
              </PriceRowPriceCaption>
              {hotelBurnResult.enabled && <OfferPageBurnPriceDisplay
                totalPrice={totalPrices.totals.price}
                pointsToApply={hotelBurnResult.pointsToBurn}
                priceInPoints={hotelBurnResult.priceInPoints}
                pointsHideZeroCash={hotelBurnResult.hideZeroCash}
                memberPrice={totalPrices.totals.memberPrice}
                size="M"
                saleUnit={offer.displayPricingAsPerPerson ? 'person' : 'total'}
              />}
              {!hotelBurnResult.enabled && <LuxPlusPriceGuard
                showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                fallbackPrice={<PriceRowPrice
                  data-testid="bedbank-package-price"
                  size="M"
                  price={totalPrices.totals.price}
                  saleUnit={saleUnit}
                />}
              >
                <LuxPlusPriceStack
                  size="M"
                  price={totalPrices.totals.price}
                  memberPrice={totalPrices.totals.memberPrice}
                  saleUnit={saleUnit}
                />
              </LuxPlusPriceGuard>}
              {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
              {!hasPromoRate && <PriceRowTaxesAndFees />}

              {roomsWithData.map(roomData => {
                return (
                  <PriceRowAgentHubCommission
                    key={roomData.room.id}
                    productType={offer.productType}
                    offerLocations={[]}
                    offerId={offer.id}
                    packageId={roomData.effectiveRate.id}
                    offerTotal={totalsByRoom[roomData.room.id].original.price}
                    kind="plain"
                  />
                )
              })}

              {!!discount && <CSSBreakpoint max="mobile">
                <PriceRowValueDiscountWithCaption
                  size="M"
                  originalValue={totalPrices.totals.value}
                  discountPercentage={discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? discount : undefined}
                  discountType="estimate"
                />
              </CSSBreakpoint>}
            </div>
            <LoyaltyPointsWrapper>
              <LoyaltyPointsEarnWithBreakdownModal
                calculationRequests={pointsEarnCalculationRequests}
                calculationType={!checkIn ? 'estimate' : 'exact'}
                pointsToApply={hotelBurnResult.pointsToBurn}
                burnPriceInPoints={hotelBurnResult.priceInPoints}
              />
            </LoyaltyPointsWrapper>
          </VerticalSpacer>
        </AccommodationPackageTilePricingBlock>
      </VerticalSpacer>)
  }
}

type MappedProps = Pick<Props,
  | 'luxPlusLPPEnabled'
  | 'isLoggedIn'
  | 'isFlightsSelected'
  | 'checkIn'
  | 'flightsEnabled'
  | 'flightOrigin'
  | 'offer'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(appState),
  isLoggedIn: !!appState.auth.account.memberId,
  isFlightsSelected: state.flights,
  checkIn: state.checkInDate,
  flightsEnabled: offerFlightsEnabled(state, appState),
  flightOrigin: state.airport,
  offer: state.bedbankOffer!,
}), BedbankMultiRoomPricing)
